<template>
  <TooltipProvider
    :delay-duration="delay"
    :disabled="disabled"
    v-bind="{
      disableClosingTrigger: $attrs.disableClosingTrigger,
      disableHoverableContent: $attrs.disableHoverableContent,
      ignoreNonKeyboardFocus: $attrs.ignoreNonKeyboardFocus,
    }"
  >
    <TooltipRoot
      v-bind="{
        disableClosingTrigger: $attrs.disableClosingTrigger,
      }"
      v-model:open="open"
      @update:open="$emit('update:open', $event)"
    >
      <TooltipTrigger v-if="useCustomSlot" :as="triggerAs" :class="props.class">
        <slot />
      </TooltipTrigger>
      <span v-else class="w-full flex" :class="props.class">
        <TooltipTrigger class="w-full" :as="triggerAs">
          <slot>
            <Icon
              name="question-mark-circle"
              class="fg-quinary hover:fg-quinary_hover"
            />
          </slot>
        </TooltipTrigger>
      </span>
      <TooltipPortal>
        <TooltipContent
          :class="cn(contentVariants({ size }), props.class)"
          :side="_side"
          :align="align"
          :alignOffset="-12"
          :sideOffset="2"
          v-bind="{
            sticky: $attrs.sticky,
          }"
        >
          <slot name="content">
            <p class="text-white text-style-xs font-semibold text-pretty">
              {{ title }}
            </p>
            <p
              v-if="description"
              class="tooltip-supporting-text text-style-xs font-medium"
            >
              {{ description }}
            </p>
          </slot>
          <TooltipArrow v-if="arrow" :width="16" :height="10" :as-child="true">
            <TooltipIcon class="-mt-0.5" />
          </TooltipArrow>
        </TooltipContent>
      </TooltipPortal>
    </TooltipRoot>
  </TooltipProvider>
</template>

<script setup>
import {
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
  TooltipPortal,
  TooltipContent,
  TooltipArrow,
} from "radix-vue";
import { Icon } from "@/SharedComponents/Icon";
import { ref, watch, computed } from "vue";
import { directionStore } from "@/Stores/direction";
import {
  TooltipArrow as TooltipIcon,
  contentVariants,
  sizes,
} from "@/SharedComponents/Tooltips";
import { cn } from "@/Utils/cn";

const useDirectionStore = directionStore();

defineEmits(["update:open"]);

const props = defineProps({
  title: {
    type: String,
    required: false,
  },
  description: {
    type: String,
    default: "",
  },
  delay: {
    type: [Number, String],
    default: 100,
  },
  side: {
    type: String,
    default: "top",
    validator: (val) => ["top", "right", "bottom", "left"].includes(val),
  },
  align: {
    type: String,
    default: "center",
    validator: (val) => ["start", "center", "end"].includes(val),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  open: {
    type: Boolean,
    default: false,
  },
  arrow: {
    type: Boolean,
    default: true,
  },
  class: {
    type: String,
    default: "",
  },
  size: {
    type: String,
    default: "xs",
    validator: (val) => sizes.includes(val),
  },
  triggerAs: {
    type: String,
    default: "button",
  },
  useCustomSlot: {
    type: Boolean,
    default: false,
  },
});

const _side = computed(() => {
  if (useDirectionStore.getDirection === "rtl") {
    if (props.side === "right") {
      return "left";
    }
    if (props.side === "left") {
      return "right";
    }
  }
  return props.side;
});

const open = ref(props.open);

watch(
  () => props.open,
  (val) => {
    open.value = val;
  }
);
</script>
